import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { bffAuthService } from "./services/BffAuthService";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  MobileStepper,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Lock,
  CheckCircle,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from "@mui/icons-material";

import { useAppStore } from "./app/zustandStore";
import { useTranslation } from "react-i18next";
import { sendRequestToUCServer, getCaptcha, validateCaptcha, CaptchaResponse } from "./registerAPI";
import { ROUTES, ROUTES_LIST } from "./home/constants/routes";
import { registerStyles } from "./styles/pages";

const StepRegister = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // 获取来源页面，如果没有则默认为根目录
  const redirectTo = searchParams.get('redirectTo') || ROUTES.index;
  const decodedRedirectTo = decodeURIComponent(redirectTo);

  // 步骤状态
  const [activeStep, setActiveStep] = useState(0);
  const steps = [t('register.step1.basicInfo'), t('register.step2.accountSetup'), t('register.step3.confirm')];

  // 表单状态
  const [formData, setFormData] = useState({
    username: "",
    emailAddress: "",
    password: "",
    confirmPassword: ""
  });
  
  // 验证码状态
  const [captchaData, setCaptchaData] = useState({
    captchaId: "",
    captchaCode: "",
    captchaImage: ""
  });
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  
  // UI状态
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({
    username: "",
    emailAddress: "",
    password: "",
    confirmPassword: ""
  });

  const { currentLanguage } = useAppStore();

  // 语言切换状态
  const [currentLang, setCurrentLang] = useState(currentLanguage === "enUS" ? "enUS" : "zhCN");

  // 密码强度检查
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // 验证表单字段
  const validateField = (name: string, value: string) => {
    const errors = { ...validationErrors };
    
    switch (name) {
      case "username":
        if (!value.trim()) {
          errors.username = t('register.usernameRequired');
        } else if (value.length < 3) {
          errors.username = t('register.usernameMinLength');
        } else {
          errors.username = "";
        }
        break;
        
      case "emailAddress":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) {
          errors.emailAddress = t('register.emailRequired');
        } else if (!emailRegex.test(value)) {
          errors.emailAddress = t('register.emailInvalid');
        } else {
          errors.emailAddress = "";
        }
        break;
        
      case "password":
        if (!value.trim()) {
          errors.password = t('register.passwordRequired');
        } else if (value.length < 8) {
          errors.password = t('register.passwordMinLength');
        } else {
          errors.password = "";
        }
        break;
        
      case "confirmPassword":
        if (!value.trim()) {
          errors.confirmPassword = t('register.confirmPasswordRequired');
        } else if (value !== formData.password) {
          errors.confirmPassword = t('register.passwordsNotMatch');
        } else {
          errors.confirmPassword = "";
        }
        break;
    }
    
    setValidationErrors(errors);
  };

  // 检查密码强度
  useEffect(() => {
    const password = formData.password;
    setPasswordStrength({
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [formData.password]);

  // 检查当前步骤是否完成
  const isStepComplete = (step: number) => {
    switch (step) {
      case 0: // 基本信息
        return (
          formData.username.trim() &&
          formData.emailAddress.trim() &&
          !validationErrors.username &&
          !validationErrors.emailAddress
        );
      case 1: // 账号设置
        return (
          formData.password.trim() &&
          formData.confirmPassword.trim() &&
          formData.password === formData.confirmPassword &&
          !validationErrors.password &&
          !validationErrors.confirmPassword &&
          Object.values(passwordStrength).every(condition => condition)
        );
      case 2: // 验证确认
        return isCaptchaValid; // 只有验证码正确才能继续
      default:
        return false;
    }
  };

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
    setError("");
  };

  // 处理下一步
  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    }
  };

  // 处理上一步
  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  // 获取验证码
  const fetchCaptcha = async () => {
    try {
      const captchaResponse: CaptchaResponse = await getCaptcha();
      setCaptchaData({
        captchaId: captchaResponse.captchaId,
        captchaCode: "",
        captchaImage: captchaResponse.imageBase64
      });
      setIsCaptchaValid(false);
    } catch (error) {
      console.error("获取验证码失败:", error);
      setError(t('register.captchaFetchFailed'));
    }
  };

  // 组件加载时自动获取验证码
  useEffect(() => {
    fetchCaptcha();
  }, []);

  // 验证验证码
  const validateCaptchaCode = async (captchaCodeValue?: string) => {
    const codeToValidate = captchaCodeValue || captchaData.captchaCode;
    if (!captchaData.captchaId || !codeToValidate) {
      setError(t('register.captchaRequired'));
      return false;
    }
    
    try {
      const isValid = await validateCaptcha(captchaData.captchaId, codeToValidate);
      setIsCaptchaValid(isValid);
      if (!isValid) {
        setError(t('register.captchaInvalid'));
      }
      return isValid;
    } catch (error) {
      console.error("验证码验证失败:", error);
      setError(t('register.captchaValidationFailed'));
      return false;
    }
  };

  // 处理注册请求
  const handleRegister = async () => {
    setIsLoading(true);
    setError("");
    
    // 验证验证码
    if (!isCaptchaValid) {
      const isValid = await validateCaptchaCode();
      if (!isValid) {
        setIsLoading(false);
        return;
      }
    }
    
    try {
      const response = await sendRequestToUCServer(
        formData.username,
        formData.emailAddress,
        formData.password,
        formData.confirmPassword,
        "", // realName - 需要从表单获取
        "", // phone - 需要从表单获取
        captchaData.captchaId,
        captchaData.captchaCode
      );
      
      if (response && response.success) {
        // 注册成功，跳转到确认页面，传递邮箱地址
        navigate(`${ROUTES_LIST.registerConfirm}?redirectTo=${encodeURIComponent(decodedRedirectTo)}&email=${encodeURIComponent(formData.emailAddress)}`);
      } else {
        setError(response?.message || t('register.registerFailed'));
      }
    } catch (error) {
      console.error("注册失败:", error);
      setError(t('register.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  // 处理登录跳转
  const handleLoginClick = () => {
    console.log(`${t('register.triggeringLoginFlow')}:`, decodedRedirectTo);
    window.location.href = '/login';
  };

  // 处理语言切换
  const handleLanguageChange = (event: React.MouseEvent<HTMLElement> | null, newLang: any) => {
    if (newLang !== null) {
      setCurrentLang(newLang);
      i18n.changeLanguage(newLang === "zhCN" ? "zh-CN" : "en-US");
    }
  };

  // 步骤内容
  const getStepContent = (step: number) => {
    switch (step) {
      case 0: // 基本信息
        return (
          <Box sx={registerStyles.form}>
            <TextField
              name="username"
              placeholder={t('register.username')}
              value={formData.username}
              onChange={handleInputChange}
              error={!!validationErrors.username}
              helperText={validationErrors.username}
              fullWidth
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                )
              }}
              sx={registerStyles.textField}
            />

            <TextField
              name="emailAddress"
              placeholder={t('register.email')}
              type="email"
              value={formData.emailAddress}
              onChange={handleInputChange}
              error={!!validationErrors.emailAddress}
              helperText={validationErrors.emailAddress}
              fullWidth
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                )
              }}
              sx={registerStyles.textField}
            />
          </Box>
        );
      
      case 1: // 账号设置
        return (
          <Box sx={registerStyles.form}>
            <TextField
              name="password"
              placeholder={t('register.password')}
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              error={!!validationErrors.password}
              helperText={validationErrors.password}
              fullWidth
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={registerStyles.textField}
            />

            {/* 密码强度指示器 */}
            {formData.password && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('register.passwordStrength')}:
                </Typography>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {Object.entries(passwordStrength).map(([key, isValid]) => (
                    <Box key={key} display="flex" alignItems="center" gap={1}>
                      <CheckCircle 
                        fontSize="small" 
                        color={isValid ? "success" : "disabled"} 
                      />
                      <Typography 
                        variant="caption" 
                        color={isValid ? "success.main" : "text.secondary"}
                      >
                        {t(`register.passwordRequirements.${key}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              name="confirmPassword"
              placeholder={t('register.confirmPassword')}
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              error={!!validationErrors.confirmPassword}
              helperText={validationErrors.confirmPassword}
              fullWidth
              required
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={registerStyles.textField}
            />
          </Box>
        );
      
      case 2: // 验证确认
        return (
          <Box sx={registerStyles.form}>
            <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('register.username')}:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formData.username}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('register.email')}:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formData.emailAddress}
                  </Typography>
                </Box>
              </Box>
            </Paper>
            
            {/* 验证码区域 */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box flex={1}>
                  <TextField
                    value={captchaData.captchaCode}
                    onChange={(e) => {
                      // 限制输入长度为4个字符
                      const inputValue = e.target.value.slice(0, 4);
                      setCaptchaData(prev => ({ ...prev, captchaCode: inputValue }));
                      setError("");
                      // 输入验证码后立即验证 - 使用当前输入值而不是状态值
                      if (inputValue.length === 4) {
                        validateCaptchaCode(inputValue);
                      } else {
                        setIsCaptchaValid(false);
                      }
                    }}
                    error={!!error && error.includes(t('register.captcha'))}
                    helperText={error && error.includes(t('register.captcha')) ? error : ""}
                    fullWidth
                    required
                    size="small"
                    placeholder={t('register.enterCaptcha')}
                    sx={{ 
                      '& .MuiInputBase-root': { 
                        height: 40 
                      } 
                    }}
                  />
                </Box>
                
                <Box>
                  {captchaData.captchaImage ? (
                    <Box 
                      component="img" 
                      src={captchaData.captchaImage} 
                      alt="验证码"
                      sx={{ 
                        width: 120, 
                        height: 40, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        borderRadius: 1,
                        cursor: 'pointer'
                      }}
                      onClick={fetchCaptcha}
                      title={t('register.clickToRefresh')}
                    />
                  ) : (
                    <Button 
                      variant="outlined" 
                      onClick={fetchCaptcha}
                      sx={{ height: 40 }}
                    >
                      {t('register.getCaptcha')}
                    </Button>
                  )}
                </Box>
              </Box>
              
              {isCaptchaValid && (
                <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                  {t('register.captchaValid')}
                </Typography>
              )}
            </Box>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #1a2980, #26d0ce)',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* 语言切换器 - 放置在页面右上角，与登录页面保持一致 */}
      <Box sx={{
        position: 'absolute',
        top: 20,
        right: 20,
        zIndex: 10
      }}>
        <Button 
          onClick={() => handleLanguageChange(null, currentLang === "zhCN" ? "enUS" : "zhCN")}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            color: 'primary.main',
            borderRadius: '6px',
            padding: '4px 10px',
            minWidth: 'auto',
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(26, 41, 128, 0.2)',
            opacity: 0.8,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(26, 41, 128, 0.4)',
              opacity: 1
            }
          }}
        >
          {currentLang === "zhCN" ? "EN" : "中文"}
        </Button>
      </Box>

      <Container maxWidth={false} sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        position: 'relative',
        zIndex: 1
      }}>
        <Paper elevation={8} sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          display: { xs: 'block', md: 'flex' },
          width: { xs: '100%', md: 900 },
          minHeight: { xs: 'auto', md: 500 },
          position: 'relative',
          zIndex: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)'
          }
        }}>
          {/* 左侧Logo和信息区域 - 只在web端显示 */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            background: 'linear-gradient(135deg, #1a2980, #26d0ce)',
            color: 'white',
            padding: 3,
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* 主要内容区域 - 垂直居中 */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1
            }}>
              {/* Logo */}
              <Box 
                component="img" 
                src="/logo_ccdtn.png" 
                alt="CCDTN Logo" 
                sx={{ 
                  width: 280,
                  height: 'auto',
                  maxHeight: 220,
                  mb: 3,
                  filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1))',
                  objectFit: 'contain'
                }} 
              />
              <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                {t('register.createAccount')}
              </Typography>
            </Box>
            
            {/* "或"分隔线及其以下内容 - 只在web端显示 */}
            <Box sx={{ width: '100%' }}>
              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.3)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {t('register.or')}
                </Typography>
              </Divider>

              {/* 登录链接 */}
              <Box textAlign="center" mb={2}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {t('register.alreadyHaveAccount')}{' '}
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={handleLoginClick}
                    sx={{ 
                      fontWeight: 'bold', 
                      color: 'rgba(255, 255, 255, 0.9)',
                      textDecoration: 'underline',
                      '&:hover': {
                        color: 'white'
                      }
                    }}
                  >
                    {t('button.login')}
                  </Link>
                </Typography>
              </Box>

              {/* 隐私条款 */}
              <Box textAlign="center">
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('register.agreeToTerms')}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* 右侧表单区域 */}
          <Box sx={{
            flex: 1,
            padding: { xs: 3, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            {/* 移动端标题区域 */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 3 }}>
              <Box 
                component="img" 
                src="/logo_ccdtn.png" 
                alt="CCDTN Logo" 
                sx={{ 
                  width: 200,
                  height: 'auto',
                  maxHeight: 120,
                  mb: 2,
                  filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1))',
                  objectFit: 'contain'
                }} 
              />
              <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main" gutterBottom>
                {t('register.createAccount')}
              </Typography>
            </Box>

            {/* 错误提示 - 在验证确认步骤中不显示全局错误提示，因为验证码输入框已有错误提示 */}
            {error && activeStep !== 2 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* 桌面端步骤指示器 */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 4 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* 移动端步骤指示器 */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
              <MobileStepper
                variant="dots"
                steps={steps.length}
                position="static"
                activeStep={activeStep}
                nextButton={null}
                backButton={null}
                sx={{ 
                  justifyContent: 'center',
                  bgcolor: 'transparent',
                  '& .MuiMobileStepper-dot': {
                    width: 8,
                    height: 8,
                    mx: 0.5
                  }
                }}
              />
              <Typography variant="subtitle1" textAlign="center" fontWeight="bold" color="primary.main">
                {steps[activeStep]}
              </Typography>
            </Box>

            {/* 步骤内容 */}
            {getStepContent(activeStep)}

            {/* 步骤导航按钮 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0 || isLoading}
                startIcon={<KeyboardArrowLeft />}
                sx={{ minWidth: 100 }}
              >
                {t('button.back')}
              </Button>
              
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isStepComplete(activeStep)}
                  endIcon={<KeyboardArrowRight />}
                  sx={{ 
                    minWidth: 100,
                    color: 'white !important',
                    '& .MuiButton-endIcon': {
                      color: 'white !important'
                    },
                    '& .MuiButton-label': {
                      color: 'white !important'
                    }
                  }}
                >
                  {t('button.next')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleRegister}
                  disabled={isLoading || !isStepComplete(activeStep)}
                  startIcon={isLoading ? <CircularProgress size={20} /> : null}
                  sx={{ 
                    minWidth: 120,
                    color: 'white !important',
                    '& .MuiButton-startIcon': {
                      color: 'white !important'
                    },
                    '& .MuiButton-label': {
                      color: 'white !important'
                    }
                  }}
                >
                  {isLoading ? t('register.creatingAccount') : t('button.register')}
                </Button>
              )}
            </Box>

            {/* "或"分隔线及其以下内容 - 只在移动端显示 */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('register.or')}
                </Typography>
              </Divider>

              {/* 登录链接 */}
              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  {t('register.alreadyHaveAccount')}{' '}
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={handleLoginClick}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {t('button.login')}
                  </Link>
                </Typography>
              </Box>

              {/* 隐私条款 */}
              <Box textAlign="center" mt={3}>
                <Typography variant="caption" color="text.secondary">
                  {t('register.agreeToTerms')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default StepRegister;